import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { countryCodeSchema } from '@apple/utils/globalization/models';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { MarketForm } from '@apple/features/market/components/MarketForm';
import { useMarketManagement } from '@apple/features/market/hooks/useMarketManagement';
import type { MarketId } from '@apple/features/market/models';
import { marketIdSchema } from '@apple/features/market/models';
import { marketQueries } from '@apple/features/market/queries';

export const Route = createFileRoute('/_authed/_admin/manage/markets_/$marketId')({
	parseParams: ({ marketId }) => ({
		marketId: marketIdSchema.parse(Number(marketId)),
	}),
	beforeLoad: ({ context, location, params: { marketId }, cause }) => {
		requireAuth(
			{ context, location, cause },
			{
				requirePermission: 'Manzanita.Security.Features.CustomerGroupManagement.Write',
			},
		);

		return {
			getMarketQueryOptions: marketQueries.getMarket(marketId),
		};
	},
	loader: ({ context: { queryClient, getMarketQueryOptions } }) => {
		void queryClient.prefetchQuery(getMarketQueryOptions);
	},
	wrapInSuspense: true,
	component: MarketRoute,
});

function MarketRoute() {
	const goBackToList = useBackOrNavigate();
	const { t } = useTranslation('market');
	const { getMarketQueryOptions } = Route.useRouteContext();
	const marketQuery = useSuspenseQuery(getMarketQueryOptions);

	const { mutate, mutateAsync } = useMarketManagement();

	const handleDeleteMarket = useCallback(
		(marketId: MarketId) => {
			modals.openConfirmModal({
				title: t('delete.title'),
				labels: {
					confirm: t('common:buttons.confirm'),
					cancel: t('common:buttons.cancel'),
				},
				onConfirm: () =>
					mutate(
						{ action: 'delete', marketId },
						{
							onSuccess: () => void goBackToList('/manage/markets'),
						},
					),
				children: <Text>{t('delete.message')}</Text>,
			});
		},
		[t, mutate, goBackToList],
	);

	return (
		<DetailsLayout title={t('title.edit')}>
			<MarketForm
				isEdit={true}
				onSaveMarket={async market => {
					await mutateAsync(
						{
							action: 'edit',
							market: {
								...market,
								id: marketQuery.data.id,
								countryCode: countryCodeSchema.parse(market.countryCode),
							},
						},
						{
							onSuccess: () => void goBackToList('/manage/markets'),
						},
					);
				}}
				market={marketQuery.data}
				onDeleteMarket={handleDeleteMarket}
			/>
		</DetailsLayout>
	);
}
